import { ofType } from 'redux-observable';
import {
   filter,
   ignoreElements,
   map,
   mapTo,
   skip,
   switchMap,
   take,
   tap,
} from 'rxjs/operators';
import { of, merge, timer, from } from 'rxjs';

import {
   addScore,
   EMULATE_EXERCISE_FOR_TUTORIAL,
   exerciseChallengeReady,
   exerciseReady,
   INIT,
   PREPARE_NEXT_CHALLENGE,
   PREPARE_NEXT_SESSION,
   REFRESH_MODULE_QUEUE,
   SET_STAGE,
   setStage,
   SUBMIT_ANSWER,
} from '../actions';
// import { randomNumberInRange, arrayValuesToObjectKeys } from './util';
import { exerciseStages } from '../../shared/constants';
import { combineExerciseEpics } from '../combineExerciseEpics';

import { getRandomArrayElement } from '../../util/getRandomArrayElement';
import { getCurrentLevel } from '../getCurrentLevel';
import { checkLoadFromModule } from '../checkLoadFromModule';

import {
   addMixerTrackEffect,
   loadLoopCategoryCount,
   replaceQueueWithNextLevel,
   setCurrentLoop,
   setCurrentLoopFromQueue,
   setInstruments,
   setLoopMode,
   setNextLevelQueue,
   setView,
   SET_CURRENT_LOOP_FROM_QUEUE,
   setLoopModeReference,
   updateMixerTrackEffect,
} from '../../interfaces/sequencer/actions';
import { getSessionsAndAverageScore } from '../getSessionsAndAverageScore';
import { resolveLoopQueueItems } from '../resolveLoopQueueItems';
import {
   filterTypes,
   sequencerInstrumentControlTypes,
   sequencerViewTypes,
} from '../../interfaces/sequencer/constants';
import { createParameter } from '../createParameter';
import { filterTypeOrder } from '../../util/constants';
import { objectMap } from '../../util/objectMap';
import { calculateEqScore } from './calculateEqScore';
import { controllerValuesEqual } from '../../util/controllerValuesEqual';

// Pretty much follow module lifecyle,,, whenever an aux is queued, que up a loop.  Only complexity is when leveling up I think?
// Complexity: when (queued aux === current aux or no aux) and they are likely to level up, have a nextLevelQueue for that next level... don't update nextLeveQueue unless it is changin between being relevant and irrellevant
// complexity need to clear nextLevelQueue when not relevant ???
// Could only use next level queue if not aux???? users will already be subscribed if aux

// queue: [] <-- added to from current aux/next aux/main exercise as appropriate
// nextLevelQueue: [] <- only relevent if they have enough sessions that they could level up next session and current aux === next aux

//* On init, add 1 loop to queue  IFF not load from aux
//*    ---- load this
//
// on refresh aux, add 1 loop to queue... might have to await loading aux
//    ---- load this
//
//* on prepare session, update current from queue, make sure loop matches current exercise and level, and wait for loop to be loaded... load if mismatched and/or not loading/loaded
//*  update lastaccessed value on cached loop
//
//* on startNextSession/prepareNextSession? refresh queue to contain another loop IFF not load from aux
//*    ---- load this
//
// on startNextSession/prepareNextSession? IFF their sessions at currentLevel >= required sessions to level up - 1 and next aux === current aux (or no aux), then load 1? item into nextLevelQueue
//    ---- load n of these
//
// on level up -> if nextLevelQueue, replace queue with nextLevelQueue
//
//* clean up stored loops cache as progressing... max 5? 10?... don't clean up the one loaded from nextLevel... clean that up if nextLevelQueue is ceared and not sent to queue
//
// on prepareNextSession? clear next level queue if current aux doesnt equal next aux and nextlevelqueue isn't empty

// loopKits other than default should be under secure url???

const getLevel = (state) => {
   const moduleId = state.activeExercise.currentModuleId;

   const level = getCurrentLevel(state, moduleId);

   const exerciseDocument = moduleId
      ? state.activeExercise.modules[moduleId].exerciseDocument
      : state.activeExercise.exerciseDocument;

   return exerciseDocument.levels[level - 1];
};

const getDefaultFilterValue = ({ typeConfig, bandRules, bandIndex }) => {
   const enabledOptions =
      bandRules?.[bandIndex]?.type?.enabledOptions ||
      typeConfig.enabledOptions ||
      Object.values(filterTypes);
   return enabledOptions.reduce((currentLowestType, type) => {
      if (
         currentLowestType === filterTypes.peaking ||
         type === filterTypes.peaking
      ) {
         return filterTypes.peaking;
      }
      if (filterTypeOrder[currentLowestType] > filterTypeOrder[type]) {
         return type;
      }
      return currentLowestType;
   });
};

const mergeConfigs = ({
   parameter,
   defaultConfig,
   filterType,
   filterRules,
   bandIndex,
   bandRules,
}) => {
   let merged = { ...defaultConfig };

   if (bandRules?.[bandIndex]?.[parameter] != null) {
      merged = {
         ...merged,
         ...bandRules[bandIndex][parameter],
      };
   }

   if (filterRules != null) {
      filterRules.forEach((rule) => {
         // if there is a need to change incrementCount, may need to handle that differently
         if (rule.filterTypes.includes(filterType)) {
            merged = {
               ...merged,
               ...rule[parameter],
            };
         }
      });
   }

   return merged;
};

const getDefaultUserValue = ({
   parameter,
   userDefault,
   referenceValue,
   defaultConfig,
   filterType,
   filterRules,
}) => {
   if (defaultConfig?.test === false) {
      let defaultValue = referenceValue;
      if (defaultConfig.useValue != null) {
         defaultValue = defaultConfig.useValue;
      }
      if (filterRules != null) {
         filterRules.forEach((rule) => {
            // if there is a need to change incrementCount, may need to handle that differently
            if (
               rule.filterTypes.includes(filterType) &&
               rule[parameter]?.useValue
            ) {
               defaultValue = rule[parameter].useValue;
            }
         });
      }
      return defaultValue;
   }
   return userDefault;
};

const getTypeChangeSettings = ({ enabledControls, filterRules }) => {
   const typeChangeSettings = {};

   if (filterRules == null) {
      return typeChangeSettings;
   }

   {
      // for every filter rule, if it has any param with useValue that is not tested (in enabledControls), then create the settings for this
      const setValue = {};

      filterRules.forEach(({ filterTypes: ruleFilterTypes, ...params }) => {
         Object.entries(params).forEach(([param, rules]) => {
            if (
               enabledControls[param]?.test === false &&
               rules.useValue != null
            ) {
               if (setValue[param] == null) {
                  setValue[param] = objectMap(
                     filterTypes,
                     () => enabledControls[param]?.useValue
                  );
               }
               setValue[param] = ruleFilterTypes.reduce(
                  (typeValueMap, currentType) => ({
                     ...typeValueMap,
                     [currentType]: rules.useValue,
                  }),
                  setValue[param]
               );
            }
         });
      });

      if (Object.keys(setValue).length > 0) {
         typeChangeSettings.setValue = setValue;
      }
   }

   return typeChangeSettings;
};

const getFrequencyRange = ({
   previousDetune,
   minDetuneBetweenBands,
   config,
   bandIndex,
   bandCount,
   enabledControls,
   bandRules,
   sampleFrequencyRange,
   typeParams,
   filterRules,
}) => {
   let rangeLow = 0;
   let rangeHigh = 1;

   if (sampleFrequencyRange) {
      [rangeLow] = sampleFrequencyRange;
   }

   if (previousDetune != null) {
      // mult by 100 to avoid float addition rounding errs
      rangeLow = (100 * previousDetune + 100 * minDetuneBetweenBands) / 100;
   }

   let lastBandMaxDetune;

   {
      // get max detune for last band using regular fallbacks for combining configs
      lastBandMaxDetune =
         bandRules?.[bandCount - 1]?.detune?.range?.[1] ||
         enabledControls.detune?.range?.[1] ||
         1;

      // apply filter rules to further narrow down the last band's max detune
      if (filterRules != null) {
         const lastBandFilterType = typeParams[bandCount - 1].value;
         filterRules.forEach((rule) => {
            // if there is a need to change incrementCount, may need to handle that differently
            if (
               rule.filterTypes.includes(lastBandFilterType) &&
               rule.detune?.range != null
            ) {
               lastBandMaxDetune = Math.min(
                  lastBandMaxDetune,
                  rule.detune.range[1]
               );
            }
         });
      }

      if (sampleFrequencyRange) {
         lastBandMaxDetune = Math.min(
            lastBandMaxDetune,
            sampleFrequencyRange[1]
         );
      }
   }

   // the 10s are b/c float issues
   rangeHigh =
      bandCount === 1
         ? lastBandMaxDetune
         : (10 * lastBandMaxDetune -
              (bandCount - bandIndex - 1) * 10 * minDetuneBetweenBands) /
           10;

   if (config.range) {
      rangeHigh = Math.min(rangeHigh, config.range[1]);
      rangeLow = Math.max(rangeLow, config.range[0]);
   }

   return [rangeLow, rangeHigh];
};

const createEqConfig = (
   {
      bandCount,
      enabledControls,
      filterRules,
      bandRules,
      minDetuneBetweenBands,
   } = {},
   sample
) => {
   // Set values for reference parameters while ensuring there are no issues between the parameters (no overlap that could be confusing, no high passes too high, etc.)... this should be ensured with help from bandRules
   const referenceBands = [];

   let previousDetune;
   // minDetuneBetweenBands

   const typeParams = [];

   for (let i = 0; i < bandCount; i += 1) {
      typeParams.push(
         createParameter({
            exerciseParameterConfig: mergeConfigs({
               parameter: 'type',
               defaultConfig: enabledControls.type,
               bandRules,
               bandIndex: i,
            }),
            randomize: true,
            defaultValue: getDefaultFilterValue({
               typeConfig: enabledControls.type,
               bandRules,
               bandIndex: i,
            }),
         })
      );
   }

   for (let i = 0; i < bandCount; i += 1) {
      const typeParam = typeParams[i];

      const mergedDetuneConfig = mergeConfigs({
         parameter: 'detune',
         defaultConfig: enabledControls.detune,
         filterType: typeParam.value,
         filterRules,
         bandRules,
         bandIndex: i,
      });

      referenceBands.push({
         type: typeParam,
         detune: createParameter({
            exerciseParameterConfig: mergedDetuneConfig,
            randomize: true,
            defaultValue: 0.5,
            // also check sample for frequency range... make sure detune works for this range
            // range: [] (restrict range from current loop sample and/or band rules here? )
            range: getFrequencyRange({
               previousDetune,
               minDetuneBetweenBands,
               config: mergedDetuneConfig,
               bandIndex: i,
               bandCount,
               enabledControls,
               bandRules,
               sampleFrequencyRange: sample.frequencyRange,
               typeParams,
               filterRules,
            }),
         }),
         gain: createParameter({
            exerciseParameterConfig: mergeConfigs({
               parameter: 'gain',
               defaultConfig: enabledControls.gain,
               filterType: typeParam.value,
               filterRules,
               bandRules,
               bandIndex: i,
            }),
            randomize: true,
            defaultValue: 0,
            bipolar: true,
         }),
         Q: createParameter({
            exerciseParameterConfig: mergeConfigs({
               parameter: 'Q',
               defaultConfig: enabledControls.Q,
               filterType: typeParam.value,
               filterRules,
               bandRules,
               bandIndex: i,
            }),
            randomize: true,
            defaultValue: 0.5,
         }),
      });

      previousDetune = referenceBands[i].detune.value;
   }

   // Sort these by frequency... no, freq should be gotten relative to (greater than) previous band

   // Set values for user parameters... either from reference when test = false, or to default value
   const userBands = [];

   referenceBands.forEach((band, i) => {
      const typeParam = createParameter({
         exerciseParameterConfig: mergeConfigs({
            parameter: 'type',
            defaultConfig: enabledControls.type,
            bandRules,
            bandIndex: i,
         }),
         // default values based on bandCount and current index if test, otherwise value from reference
         defaultValue:
            enabledControls.type?.test === false
               ? band.type.value
               : getDefaultFilterValue({
                    typeConfig: enabledControls.type,
                    bandRules,
                    bandIndex: i,
                 }),
      });

      userBands.push({
         type: typeParam,
         detune: createParameter({
            exerciseParameterConfig: mergeConfigs({
               parameter: 'detune',
               defaultConfig: enabledControls.detune,
               filterType: band.type.value,
               filterRules,
               bandRules,
               bandIndex: i,
            }),
            defaultValue: getDefaultUserValue({
               parameter: 'detune',
               userDefault: bandRules?.[i]?.detune?.defaultUserValue ?? 0.5,
               referenceValue: band.detune.value,
               defaultConfig: mergeConfigs({
                  parameter: 'detune',
                  defaultConfig: enabledControls.detune,
                  filterType: band.type.value,
                  filterRules,
                  bandRules,
                  bandIndex: i,
               }),
               filterType: typeParam.value,
               filterRules,
            }),
         }),
         gain: createParameter({
            exerciseParameterConfig: mergeConfigs({
               parameter: 'gain',
               defaultConfig: enabledControls.gain,
               filterType: band.type.value,
               filterRules,
               bandRules,
               bandIndex: i,
            }),

            defaultValue: getDefaultUserValue({
               parameter: 'gain',
               userDefault: 0,
               referenceValue: band.gain.value,
               defaultConfig: mergeConfigs({
                  parameter: 'gain',
                  defaultConfig: enabledControls.gain,
                  filterType: band.type.value,
                  filterRules,
                  bandRules,
                  bandIndex: i,
               }),
               filterType: typeParam.value,
               filterRules,
            }),
            bipolar: true,
         }),
         Q: createParameter({
            exerciseParameterConfig: mergeConfigs({
               parameter: 'Q',
               defaultConfig: enabledControls.Q,
               filterType: band.type.value,
               filterRules,
               bandRules,
               bandIndex: i,
            }),
            defaultValue: getDefaultUserValue({
               parameter: 'Q',
               userDefault: 0.5,
               referenceValue: band.Q.value,
               defaultConfig: mergeConfigs({
                  parameter: 'Q',
                  defaultConfig: enabledControls.Q,
                  filterType: band.type.value,
                  filterRules,
                  bandRules,
                  bandIndex: i,
               }),
               filterType: typeParam.value,
               filterRules,
            }),
         }),
         typeChangeSettings: getTypeChangeSettings({
            enabledControls,
            filterRules,
         }),
      });
   });

   const referenceConfig = {
      type: 'eq',
      settings: {
         bands: referenceBands,
      },
   };

   const userConfig = {
      type: 'eq',
      settings: {
         bands: userBands,
      },
   };

   return {
      userConfig,
      referenceConfig,
   };
};

const selectSamples = (loop) => [
   getRandomArrayElement(Object.keys(loop.samples)),
];

const createMixer = (loop, level) => {
   const sampleIds = selectSamples(loop, level.trackCount);

   return sampleIds.map((id) => {
      const { userConfig, referenceConfig } = createEqConfig(
         level,
         loop.samples[id]
      );

      let pregainAdjustment = 1;

      if (level.autoPregain) {
         referenceConfig.settings.bands.forEach(({ type, gain, Q, detune }) => {
            if (
               type.value === filterTypes.lowshelf ||
               type.value === filterTypes.highshelf
            ) {
               let reduceGainBy;

               reduceGainBy = Math.min(gain.value, 0.8);

               if (
                  type.value === filterTypes.highshelf &&
                  detune.value > 0.75
               ) {
                  reduceGainBy *= 0.75;
               }
               if (type.value === filterTypes.lowshelf && detune.value < 0.25) {
                  reduceGainBy *= 0.75;
               }

               pregainAdjustment = Math.min(
                  1 - reduceGainBy,
                  pregainAdjustment
               );
            } else if (type.value === filterTypes.peaking) {
               let reduceGainBy;

               if (gain.value > 0.75) {
                  reduceGainBy = 0.75;
               } else if (gain.value > 0.5) {
                  reduceGainBy = 0.5;
               } else if (gain.value > 0.25) {
                  reduceGainBy = 0.25;
               } else {
                  reduceGainBy = 0;
               }

               if (Q.value > 0.66) {
                  reduceGainBy *= 1;
               } else if (Q.value > 0.5) {
                  reduceGainBy *= 0.75;
               } else if (Q.value > 0.33) {
                  reduceGainBy *= 0.5;
               } else if (Q.value > 0.15) {
                  reduceGainBy *= 0.25;
               } else {
                  reduceGainBy *= 0;
               }

               pregainAdjustment = Math.min(
                  1 - reduceGainBy,
                  pregainAdjustment
               );
            }
         });
      }

      return {
         source: id,
         referenceMixerTrack: {
            pan: createParameter({
               defaultValue: 0,
               bipolar: true,
            }),
            gain: createParameter({
               defaultValue: 1,
            }),
            effects: [referenceConfig],
         },

         mixerTrack: {
            pan: createParameter({
               defaultValue: 0,
               bipolar: true,
            }),
            gain: createParameter({
               defaultValue: 1,
            }),
            effects: [userConfig],
         },
         pregain:
            pregainAdjustment * (level.pregain || loop.samples[id].pregain),
      };
   });
};

const configureInterfaceOnInitEpic = (action$) =>
   action$.pipe(
      ofType(INIT),
      switchMap(() =>
         merge(
            of(
               setInstruments([
                  {
                     source: 'Kick',
                     name: 'Kick',
                     controlType: sequencerInstrumentControlTypes.stepSequencer,
                  },
               ])
            ),
            of(setLoopMode()),
            of(
               addMixerTrackEffect(0, {
                  type: 'eq',
                  settings: {
                     bands: [
                        {
                           type: { value: filterTypes.lowshelf },
                           detune: { value: 0 },
                           gain: { value: 0 },
                           Q: { value: 1 },
                        },
                        {
                           type: { value: filterTypes.peaking },
                           detune: { value: 0.25 },
                           gain: { value: 0 },
                           Q: { value: 1 },
                        },
                        {
                           type: { value: filterTypes.peaking },
                           detune: { value: 0.5 },
                           gain: { value: 0 },
                           Q: { value: 1 },
                        },
                        {
                           type: { value: filterTypes.peaking },
                           detune: { value: 0.75 },
                           gain: { value: 0 },
                           Q: { value: 1 },
                        },
                        {
                           type: { value: filterTypes.highshelf },
                           detune: { value: 1 },
                           gain: { value: 0 },
                           Q: { value: 1 },
                        },
                     ],
                  },
               })
            ),
            of(
               setView({
                  viewType: sequencerViewTypes.mixerEffect,
                  instrumentIndex: 0,
                  effectIndex: 0,
               })
            )
         )
      )
   );

// On init and start next challenge, add 1 loop to queue  IFF not load from aux
const maintainQueueEpic = (action$, state$) =>
   action$.pipe(
      ofType(INIT, SET_CURRENT_LOOP_FROM_QUEUE),
      filter(
         (action) =>
            !checkLoadFromModule(state$.value) &&
            // If action is init, activeInterface isn't in state yet and queue will be 0
            (action.type === INIT ||
               state$.value.activeInterface.loops.queue.length === 0)
      ),
      // await selected loopKits
      // then add some loops to queue, 2 should be loaded in background... await one of these when preparing next challenge, and keep 2 loaded always
      switchMap((action) =>
         timer(0, 100).pipe(
            filter(() => {
               const { selectedLoopKits } = state$.value.appData;
               return selectedLoopKits && !selectedLoopKits.loading;
            }),
            take(1),
            switchMap(() => {
               const currentState = state$.value;
               const level = getCurrentLevel(currentState);

               const { selectedLoopKits } = currentState.appData;

               const categories =
                  currentState.activeExercise.exerciseDocument.levels[level - 1]
                     .loopCategories;

               const queueItemsToResolve = [
                  {
                     loopKitId: getRandomArrayElement(selectedLoopKits.global),
                     categoryId: getRandomArrayElement(categories),
                  },
               ];

               const nextLevelQueueItemsToResolve = [];

               const levelUpRequirements =
                  currentState.activeExercise.exerciseDocument.levels[level - 1]
                     .lvlUpRequirements;

               const { sessionsAtLevel, averageSessionScore } =
                  getSessionsAndAverageScore(
                     currentState.activeExercise,
                     currentState.activeExercise.progress.sessionHistory
                  );

               const bestPossibleAverageScore =
                  (averageSessionScore * sessionsAtLevel + 100) /
                  (sessionsAtLevel + 1);

               if (
                  action.type === SET_CURRENT_LOOP_FROM_QUEUE &&
                  // Don't need to load for next level if a specific level is selected
                  currentState.activeExercise.levelSelect == null &&
                  //
                  levelUpRequirements &&
                  sessionsAtLevel + 1 >= levelUpRequirements.minSessions &&
                  bestPossibleAverageScore >= levelUpRequirements.minAvgScore &&
                  currentState.activeInterface.loops.nextLevelQueue.length ===
                     0 &&
                  level <
                     currentState.activeExercise.exerciseDocument.levelCount &&
                  !currentState.activeExercise.exerciseDocument.levels[level]
                     .loadFromModule
               ) {
                  const nextLevelCategories =
                     currentState.activeExercise.exerciseDocument.levels[level]
                        .loopCategories;

                  nextLevelQueueItemsToResolve.push({
                     loopKitId: getRandomArrayElement(selectedLoopKits.global),
                     categoryId: getRandomArrayElement(nextLevelCategories),
                  });
               }

               const countsToLoad = Object.values(
                  queueItemsToResolve
                     .concat(nextLevelQueueItemsToResolve)
                     .reduce((itemMap, currentItem) => {
                        const key = `${currentItem.loopKitId}/${currentItem.categoryId}`;
                        // this check is not necessary on init, but is on nextchallenge or whatever
                        if (
                           currentState.activeInterface.loops.categoryCounts[
                              key
                           ] == null
                        ) {
                           return {
                              ...itemMap,
                              [key]: currentItem,
                           };
                        }
                        return itemMap;
                     }, {})
               );

               return merge(
                  // get loop counts
                  from(countsToLoad).pipe(
                     map((item) => loadLoopCategoryCount(item))
                  ),
                  // resolve and add to queue after awaiting loop count
                  resolveLoopQueueItems({ state$, queueItemsToResolve }),
                  resolveLoopQueueItems({
                     state$,
                     queueItemsToResolve: nextLevelQueueItemsToResolve,
                     nextLevel: true,
                  })
               );
            })
         )
      )
   );

const maintainQueueWhenIsModuleEpic = (action$, state$) =>
   action$.pipe(
      ofType(REFRESH_MODULE_QUEUE),
      filter(
         () =>
            checkLoadFromModule(state$.value) &&
            state$.value.activeInterface.loops.queue.length === 0
      ),
      switchMap(() =>
         timer(0, 100).pipe(
            // await module loaded and loopKits loaded
            filter(() => {
               const aux =
                  state$.value.activeExercise.modules[
                     state$.value.activeExercise.moduleQueue[0]
                  ];
               const { selectedLoopKits } = state$.value.appData;
               return (
                  selectedLoopKits &&
                  !selectedLoopKits.loading &&
                  aux &&
                  !aux.loading
               );
            }),
            take(1),
            switchMap(() => {
               const currentState = state$.value;

               const moduleExercise =
                  currentState.activeExercise.modules[
                     currentState.activeExercise.moduleQueue[0]
                  ];

               const level = getCurrentLevel(
                  currentState,
                  currentState.activeExercise.moduleQueue[0]
               );

               const { selectedLoopKits } = currentState.appData;

               const categories =
                  moduleExercise.exerciseDocument.levels[level - 1]
                     .loopCategories;

               const queueItemsToResolve = [
                  {
                     loopKitId: getRandomArrayElement(selectedLoopKits.global),
                     categoryId: getRandomArrayElement(categories),
                  },
               ];

               const nextLevelQueueItemsToResolve = [];

               const levelUpRequirements =
                  moduleExercise.exerciseDocument.levels[level - 1]
                     .lvlUpRequirements;

               const { sessionsAtLevel, averageSessionScore } =
                  getSessionsAndAverageScore(
                     moduleExercise,
                     moduleExercise.progress.sessionHistory
                  );

               const bestPossibleAverageScore =
                  (averageSessionScore * sessionsAtLevel + 100) /
                  (sessionsAtLevel + 1);

               if (
                  levelUpRequirements &&
                  sessionsAtLevel + 1 >= levelUpRequirements.minSessions &&
                  bestPossibleAverageScore >= levelUpRequirements.minAvgScore &&
                  currentState.activeInterface.loops.nextLevelQueue.length ===
                     0 &&
                  level < moduleExercise.exerciseDocument.levelCount &&
                  // check next aux is same as current
                  currentState.activeExercise.currentModuleId ===
                     currentState.activeExercise.moduleQueue[0]
               ) {
                  const nextLevelCategories =
                     moduleExercise.exerciseDocument.levels[level]
                        .loopCategories;

                  nextLevelQueueItemsToResolve.push({
                     loopKitId: getRandomArrayElement(selectedLoopKits.global),
                     categoryId: getRandomArrayElement(nextLevelCategories),
                  });
               }

               const countsToLoad = Object.values(
                  queueItemsToResolve
                     .concat(nextLevelQueueItemsToResolve)
                     .reduce((itemMap, currentItem) => {
                        const key = `${currentItem.loopKitId}/${currentItem.categoryId}`;
                        // this check is not necessary on init, but is on nextchallenge or whatever
                        if (
                           currentState.activeInterface.loops.categoryCounts[
                              key
                           ] == null
                        ) {
                           return {
                              ...itemMap,
                              [key]: currentItem,
                           };
                        }
                        return itemMap;
                     }, {})
               );

               return merge(
                  // get loop counts
                  from(countsToLoad).pipe(
                     map((item) => loadLoopCategoryCount(item))
                  ),
                  // resolve and add to queue after awaiting loop count
                  resolveLoopQueueItems({ state$, queueItemsToResolve }),
                  resolveLoopQueueItems({
                     state$,
                     queueItemsToResolve: nextLevelQueueItemsToResolve,
                     nextLevel: true,
                  })
               );
            })
         )
      )
   );

const clearNextLevelQueueEpic = (action$, state$) =>
   action$.pipe(
      ofType(REFRESH_MODULE_QUEUE),
      filter(
         () =>
            checkLoadFromModule(state$.value) &&
            state$.value.activeExercise.currentModuleId !==
               state$.value.activeExercise.moduleQueue[0] &&
            state$.value.activeInterface.loops.nextLevelQueue.length > 0
      ),
      // clear that queue
      mapTo(setNextLevelQueue([]))
   );

// wait for an item to be in queue before starting session
const prepareNextSessionEpic = (action$, state$) =>
   action$.pipe(
      ofType(PREPARE_NEXT_SESSION),

      switchMap(() =>
         timer(0, 100).pipe(
            filter(() => state$.value.activeInterface.loops.queue.length > 0),
            take(1)
         )
      ),

      switchMap(() =>
         merge(
            timer(0, 100).pipe(
               filter(() => {
                  const loopFromQueue =
                     state$.value.activeInterface.loops.queue[0];
                  const loopPath = `${loopFromQueue.loopKitId}/${loopFromQueue.categoryId}/${loopFromQueue.loopId}`;

                  const loop =
                     state$.value.activeInterface.loops.cache[loopPath];
                  return loop && !loop.loading;
               }),
               take(1),
               mapTo(exerciseReady())
            ),
            action$.pipe(
               ofType(PREPARE_NEXT_CHALLENGE),
               take(1),
               switchMap(() => of(setCurrentLoopFromQueue()))
            )
         )
      )
   );

// on prepare challenge, update current from queue, and wait for loop to be loaded...
const prepareNextChallengeEpic = (action$, state$) =>
   action$.pipe(
      ofType(PREPARE_NEXT_SESSION),
      switchMap(() =>
         merge(
            action$.pipe(ofType(SET_CURRENT_LOOP_FROM_QUEUE), take(1)),
            action$.pipe(ofType(PREPARE_NEXT_CHALLENGE), skip(1))
         )
      ),
      // switchMap(() =>
      //    merge(
      //       of(setCurrentLoopFromQueue()),
      //       action$.pipe(
      //          ofType(SET_CURRENT_LOOP_FROM_QUEUE),
      //          take(1),
      //          switchMap(() => timer(0, 100)),
      //          filter(() => {
      //             const loop =
      //                state$.value.activeInterface.loops.cache[
      //                   state$.value.activeInterface.loops.currentLoop
      //                ];
      //             return loop && !loop.loading;
      //          }),
      //          take(1),
      //          mapTo(exerciseChallengeReady())
      //       )
      //    )
      // ),

      switchMap(() =>
         merge(
            // clean this the F up, use get level... handle module b/c this will be used in EQ too
            of(
               setInstruments(
                  createMixer(
                     state$.value.activeInterface.loops.cache[
                        state$.value.activeInterface.loops.currentLoop
                     ],
                     getLevel(state$.value)
                  )
               )
            ),
            of(exerciseChallengeReady())
         )
      )

      // mapTo(exerciseChallengeReady()),
   );

const replaceQueueWithNextLevelEpic = (action$, state$) =>
   action$.pipe(
      ofType(SET_STAGE),
      filter((action) => {
         const currentState = state$.value;

         return (
            action.stage === exerciseStages.levelUp &&
            (currentState.activeExercise.currentModuleId == null ||
               currentState.activeExercise.currentModuleId ===
                  currentState.activeExercise.moduleQueue[0])
         );
      }),
      mapTo(replaceQueueWithNextLevel())
   );

const emulateForTutorialEpic = (action$, state$) =>
   action$.pipe(
      ofType(EMULATE_EXERCISE_FOR_TUTORIAL),
      switchMap(() => {
         const loop = state$.value.activeInterface.loops.queue[0];
         const loopPath = `${loop.loopKitId}/${loop.categoryId}/${loop.loopId}`;
         return merge(
            of(setCurrentLoop(loopPath)),
            of(setLoopModeReference(true)),
            of(
               setInstruments(
                  createMixer(
                     state$.value.activeInterface.loops.cache[loopPath],
                     getLevel(state$.value)
                  )
               )
            )
         );
      })
   );

const getPeakingFilterIndexes = (bands) =>
   bands
      .map((band, index) => ({
         type: band.type.value,
         frequency: band.detune.value,
         index,
      }))
      .filter(({ type }) => type === filterTypes.peaking)
      .sort((a, b) => (a.frequency >= b.frequency ? 1 : -1))
      .map(({ index }) => index);

const submitEQAnswerEpic = (action$, state$) =>
   action$.pipe(
      ofType(SUBMIT_ANSWER),

      switchMap(() => {
         const peakIndexes = getPeakingFilterIndexes(
            state$.value.activeInterface.instrumentRack[0].mixerTrack.effects[0]
               .settings.bands
         );

         const mergeWithEqMod = [];

         if (
            peakIndexes.length > 1 &&
            peakIndexes.findIndex((v, i) => i > 0 && v < peakIndexes[i - 1]) !==
               -1
         ) {
            let bandOrder = [
               ...state$.value.activeInterface.instrumentRack[0]
                  .referenceMixerTrack.effects[0].settings.bands,
            ];

            // In order to swap, all of the reference bands at relevant indexes must also be peaking
            let allRefIndexesArePeaking = true;
            const referencePeakMap = peakIndexes.reduce((map, index) => {
               const band = bandOrder[index];
               if (band.type.value !== filterTypes.peaking) {
                  allRefIndexesArePeaking = false;
               }
               return { ...map, [index]: band };
            }, {});

            if (allRefIndexesArePeaking) {
               let j = 0;
               bandOrder = bandOrder.map((b, i) => {
                  if (referencePeakMap[i] != null) {
                     let returnBand = referencePeakMap[peakIndexes[j]];
                     j += 1;
                     return returnBand;
                  }
                  return b;
               });
               mergeWithEqMod.push(
                  of(
                     updateMixerTrackEffect(
                        0,
                        0,
                        { bands: { $set: bandOrder } },
                        true
                     )
                  )
               );
            }

            // below code matches reference bands with user bands when their frequencies are equal

            // let i = 0;

            // while (i < bandOrder.length) {
            //    if (bandOrder[i].type.value !== filterTypes.peaking) {
            //       i += 1;
            //    } else {
            //       const userIndex =
            //          state$.value.activeInterface.instrumentRack[0].mixerTrack.effects[0].settings.bands.findIndex(
            //             (userband) =>
            //                controllerValuesEqual(
            //                   userband.detune.value,
            //                   bandOrder[i].detune.value
            //                )
            //          );
            //       if (
            //          userIndex === -1 ||
            //          userIndex === i ||
            //          bandOrder[userIndex].type.value !== filterTypes.peaking
            //       ) {
            //          i += 1;
            //       } else {
            //          let tmp = bandOrder[userIndex];
            //          bandOrder[userIndex] = bandOrder[i];
            //          bandOrder[i] = tmp;
            //       }
            //    }
            // }

            // mergeWithEqMod.push(
            //    of(
            //       updateMixerTrackEffect(
            //          0,
            //          0,
            //          { bands: { $set: bandOrder } },
            //          true
            //       )
            //    )
            // );
         }

         return merge(
            ...mergeWithEqMod,
            of(addScore(calculateEqScore(state$.value.activeInterface))),
            of(setStage(exerciseStages.viewScore))
         );
      })
   );

export const eqExerciseEpics = combineExerciseEpics(
   configureInterfaceOnInitEpic,
   maintainQueueEpic,
   prepareNextSessionEpic,
   prepareNextChallengeEpic,
   replaceQueueWithNextLevelEpic,
   maintainQueueWhenIsModuleEpic,
   clearNextLevelQueueEpic,
   emulateForTutorialEpic,
   submitEQAnswerEpic
);
